import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "../components/PageShell";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Loader2, MapPin, Briefcase } from "lucide-react";
import QualificationInput from "../components/QualificationInput";
import { toast } from "sonner";

const EMP_TYPES = ["Full-time", "Part-time", "Contract", "Locum"];

const PostRequirement = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    role_title: "", qualification: "", location: "", employment_type: "Full-time",
    experience_level: "", salary_range: "", description: "",
  });
  const [saving, setSaving] = useState(false);
  const [mine, setMine] = useState([]);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
  }, [authLoading, user, navigate]);

  const loadMine = () => {
    if (user?.role === "organisation") api.get("/jobs/mine").then((r) => setMine(r.data)).catch(() => {});
  };
  useEffect(() => { loadMine(); /* eslint-disable-next-line */ }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/jobs", form);
      toast.success("Requirement posted!", { description: "It's now live on the opportunities feed." });
      setForm({ role_title: "", qualification: "", location: "", employment_type: "Full-time", experience_level: "", salary_range: "", description: "" });
      loadMine();
    } catch (err) {
      // Handle FastAPI 422 validation errors (detail is an array)
      const detail = err?.response?.data?.detail;
      let errorMsg = "Could not post requirement";
      if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg).join(", ");
      } else if (typeof detail === "string") {
        errorMsg = detail;
      }
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || !user) return <PageShell><div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#1E4FA3]" /></div></PageShell>;

  if (user.role !== "organisation") {
    return (
      <PageShell>
        <div className="mx-auto max-w-lg px-6 py-24 text-center">
          <h1 className="font-heading text-2xl font-bold text-[#0B2545]">Organisations only</h1>
          <p className="mt-2 text-slate-600">Posting a requirement is available to healthcare organisations. You're signed in as a professional.</p>
          <Button onClick={() => navigate("/opportunities")} className="mt-6 h-11 rounded-xl bg-[#16A34A] px-6 font-semibold text-white hover:bg-[#138a3f]">Browse Opportunities</Button>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="mx-auto max-w-[1000px] px-6 py-10 lg:px-8">
        <span className="inline-block rounded-full bg-[#16A34A]/10 px-3 py-1 text-xs font-semibold text-[#16A34A]">Free to post</span>
        <h1 className="mt-3 font-heading text-[34px] font-extrabold text-[#0B2545] sm:text-[40px]">Post a Requirement</h1>
        <p className="mt-2 text-[16px] text-slate-600">List an opening and reach qualified healthcare professionals.</p>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-5">
          <form onSubmit={submit} className="lg:col-span-3 space-y-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm sm:p-7">
            <div>
              <Label htmlFor="role_title">Role title *</Label>
              <Input id="role_title" required value={form.role_title} onChange={set("role_title")} className="mt-1.5 h-11" placeholder="e.g. Staff Nurse - ICU" />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="qualification">Qualification needed *</Label>
                <QualificationInput id="qualification" required value={form.qualification} onChange={set("qualification")} className="mt-1.5 h-11" placeholder="e.g. BSc Nursing, MBBS" />
              </div>
              <div>
                <Label htmlFor="location">Location *</Label>
                <Input id="location" required value={form.location} onChange={set("location")} className="mt-1.5 h-11" placeholder="" />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label>Employment type</Label>
                <Select value={form.employment_type} onValueChange={(v) => setForm({ ...form, employment_type: v })}>
                  <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EMP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="exp">Experience level</Label>
                <Input id="exp" value={form.experience_level} onChange={set("experience_level")} className="mt-1.5 h-11" placeholder="e.g. 2+ years" />
              </div>
            </div>
            <div>
              <Label htmlFor="salary">Salary range</Label>
              <Input id="salary" value={form.salary_range} onChange={set("salary_range")} className="mt-1.5 h-11" placeholder="" />
            </div>
            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea id="description" required value={form.description} onChange={set("description")} rows={5} className="mt-1.5" placeholder="Describe the role, responsibilities and requirements..." />
            </div>
            <Button type="submit" disabled={saving} className="h-11 w-full rounded-xl bg-[#1E4FA3] text-[15px] font-semibold text-white hover:bg-[#193f83]">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Post Requirement — Free
            </Button>
          </form>

          <div className="lg:col-span-2">
            <h2 className="font-heading text-lg font-bold text-[#0B2545]">Your postings ({mine.length})</h2>
            <div className="mt-3 space-y-3">
              {mine.length === 0 && <p className="rounded-xl border border-dashed border-slate-200 bg-white p-4 text-sm text-slate-500">No postings yet. Create your first above.</p>}
              {mine.map((j) => (
                <div key={j.id} className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                  <h3 className="font-semibold text-[#0B2545]">{j.role_title}</h3>
                  <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{j.location}</span>
                    <span className="flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" />{j.employment_type}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default PostRequirement;
